import {
  AbstractControl,
  ValidationErrors,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';

// Validateur personnalisé pour le mot de passe
export function passwordValidator(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value;
  if (!value) {
    return null;
  }

  // Longueur minimale de 8 caractères
  const hasMinLength = value.length >= 8;

  // Au moins une majuscule
  const hasUpperCase = /[A-Z]/.test(value);

  // Au moins un caractère spécial
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

  // Vérifier si toutes les conditions sont remplies
  const passwordValid = hasMinLength && hasUpperCase && hasSpecialChar;

  // Retourner une erreur si le mot de passe n'est pas valide
  return !passwordValid ? { passwordStrength: true } : null;
}

// Validateur personnalisé pour le SIREN
export function sirenValidator(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value;

  // Si le champ est vide, il est valide (car facultatif)
  if (!value) {
    return null;
  }

  // Vérifier que le SIREN contient exactement 9 chiffres
  const isSirenValid = /^\d{9}$/.test(value);

  // Retourner une erreur si le SIREN n'est pas valide
  return !isSirenValid ? { sirenInvalid: true } : null;
}

// Validateur personnalisé pour le SIRET
export function siretValidator(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value;

  // Si le champ est vide, il est valide (car facultatif)
  if (!value || value.trim() === '') {
    return null;
  }

  // Vérifier que le SIRET contient exactement 14 chiffres
  const isSiretValid = /^\d{14}$/.test(value);

  // Retourner une erreur si le SIRET n'est pas valide
  return !isSiretValid ? { siretInvalid: true } : null;
}

// Validateur personnalisé pour vérifier que deux champs correspondent
export function mustMatch(controlName: string, matchingControlName: string) {
  return (abstractControl: AbstractControl): ValidationErrors | null => {
    // On s'assure que l'AbstractControl est bien un FormGroup
    const formGroup = abstractControl as FormGroup;
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];

    // Si le contrôle de correspondance a déjà une erreur qui n'est pas `mustMatch`, on ne fait rien
    if (matchingControl.errors && !matchingControl.errors.mustMatch) {
      return null;
    }

    // Si les valeurs ne correspondent pas, on ajoute une erreur `mustMatch`
    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ mustMatch: true });
    } else {
      // Sinon, on supprime l'erreur `mustMatch`
      matchingControl.setErrors(null);
    }

    return null; // Retourne null car l'erreur est directement appliquée au contrôle
  };
}

// Validateur personnalisé pour le format HH:MM (heures supérieures à 24 autorisées)
// Retourne timeFormatInvalidMinutes si les minutes > 59, timeFormatInvalid pour tout autre format invalide
export function timeFormatValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Si le champ est vide, il est valide (car facultatif)
    if (!value) {
      return null;
    }

    // Format général : 1 à 3 chiffres, deux-points, 2 chiffres
    const formatMatch = /^([0-9]{1,3}):([0-9]{2})$/.exec(value);
    if (!formatMatch) {
      return { timeFormatInvalid: true };
    }

    const minutes = parseInt(formatMatch[2], 10);
    if (minutes > 59) {
      return { timeFormatInvalidMinutes: true };
    }

    return null;
  };
}

// Validateur personnalisé pour le format HH:MM (heures supérieures à 24 non autorisées)
export function strictTimeFormatValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Si le champ est vide, il est valide (car facultatif)
    if (!value) {
      return null;
    }

    // Vérifier que la valeur correspond au format HH:MM
    const isTimeValid = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);

    // Retourner une erreur si le format n'est pas valide
    return !isTimeValid ? { strictTimeFormatInvalid: true } : null;
  };
}

export function CheckDateFormat(control: AbstractControl) {
  if (control.value == null || control.value == '') {
    return null;
  }
  const dateTab = control.value.split('/');
  const dateValue =
    dateTab.length == 3
      ? dateTab[1] + '/' + dateTab[0] + '/' + dateTab[2]
      : '--';
  const isValidDate = new Date(dateValue).toString();
  if (isValidDate == 'Invalid Date') {
    return { invalidDate: true };
  }
  return null;
}

export function isRequiredFieldFilled(form: FormGroup): boolean {
  for (const key of Object.keys(form.controls)) {
    const control = form.get(key);

    if (control && control.errors && control.errors['required']) {
      return false;
    }
  }
  return true;
}
